import { useState, useEffect, useCallback, useRef } from 'react';
import { StuckRow, HistoryPoint, TicketInfo } from './types';
import { fetchStuck } from './stuckApi';

export type DataStatus = 'loading' | 'live' | 'error' | 'refreshing';

export interface StuckData {
    rows: StuckRow[];
    history: HistoryPoint[];
    tickets: TicketInfo[];
    status: DataStatus;
    generatedAt: string;
    error: string | null;
    refresh: () => void;
}

/**
 * Loads /stuck from the backend on mount (and on stage/realm change). Starts empty; on an
 * unreachable backend it surfaces an error state (no fabricated data is ever shown).
 * `refresh` re-fetches (wired to the banner Retry action).
 */
export function useStuckData(stage: string, realm: string): StuckData {
    const [rows, setRows] = useState<StuckRow[]>([]);
    const [history, setHistory] = useState<HistoryPoint[]>([]);
    const [tickets, setTickets] = useState<TicketInfo[]>([]);
    const [status, setStatus] = useState<DataStatus>('loading');
    const [generatedAt, setGeneratedAt] = useState('');
    const [error, setError] = useState<string | null>(null);
    const mounted = useRef(true);
    const requestId = useRef(0);

    const load = useCallback((refreshing: boolean) => {
        // Guard against a stale in-flight fetch (e.g. region switched mid-request via the shared
        // env picker) resolving after a newer one and overwriting current data: ignore any
        // response whose id is no longer the latest.
        const currentId = ++requestId.current;
        setStatus(refreshing ? 'refreshing' : 'loading');
        fetchStuck(stage, realm)
            .then((d) => {
                if (!mounted.current || currentId !== requestId.current) return;
                setRows(d.rows);
                setHistory(d.history);
                setTickets(d.tickets);
                setGeneratedAt(d.generatedAt);
                setStatus('live');
                setError(null);
            })
            .catch((e) => {
                if (!mounted.current || currentId !== requestId.current) return;
                setRows([]);
                setHistory([]);
                setTickets([]);
                setStatus('error');
                setError(e instanceof Error ? e.message : String(e));
            });
    }, [stage, realm]);

    useEffect(() => {
        mounted.current = true;
        load(false);
        return () => { mounted.current = false; };
    }, [load]);

    const refresh = useCallback(() => load(true), [load]);

    return { rows, history, tickets, status, generatedAt, error, refresh };
}
